<template>
  <div class="basic-config">
    <!-- 模板信息 -->
    <ConfigSection title="模板信息" icon="📝">
      <div class="form-group">
        <label class="form-label">模板名称</label>
        <input
          v-model="templateName"
          type="text"
          class="form-control"
          placeholder="请输入模板名称"
        />
      </div>
      
      <div class="form-group">
        <label class="form-label">模板描述</label>
        <textarea
          v-model="templateDescription"
          class="form-control"
          rows="3"
          placeholder="请输入模板描述（可选）"
        ></textarea>
      </div>
    </ConfigSection>

    <!-- 纸张设置 -->
    <ConfigSection title="纸张设置" icon="📐">
      <div class="form-group">
        <label class="form-label">纸张预设</label>
        <select v-model="paperSize" class="form-control">
          <option value="A4">A4 (210×297mm)</option>
          <option value="A3">A3 (297×420mm)</option>
          <option value="A5">A5 (148×210mm)</option>
          <option value="Letter">Letter (216×279mm)</option>
          <option value="custom">自定义尺寸</option>
        </select>
      </div>
      
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">宽度 (mm)</label>
          <input
            v-model.number="paperWidth"
            type="number"
            class="form-control"
            min="10"
            max="1000"
            :disabled="paperSize !== 'custom'"
          />
        </div>
        <div class="form-group">
          <label class="form-label">高度 (mm)</label>
          <input
            v-model.number="paperHeight"
            type="number"
            class="form-control"
            min="10"
            max="1000"
            :disabled="paperSize !== 'custom'"
          />
        </div>
      </div>
      
      <div class="form-group">
        <label class="form-label">纸张方向</label>
        <div class="radio-group">
          <label class="radio-item">
            <input
              v-model="orientation"
              type="radio"
              value="portrait"
            />
            <span class="radio-label">
              <span class="radio-icon">📄</span>
              纵向
            </span>
          </label>
          <label class="radio-item">
            <input
              v-model="orientation"
              type="radio"
              value="landscape"
            />
            <span class="radio-label">
              <span class="radio-icon">📃</span>
              横向
            </span>
          </label>
        </div>
      </div>
    </ConfigSection>

    <!-- 边距设置 -->
    <ConfigSection title="边距设置" icon="📏">
      <div class="margin-controls">
        <div class="margin-visual">
          <div class="margin-preview">
            <div class="margin-top">
              <input
                v-model.number="marginTop"
                type="number"
                min="0"
                max="100"
                class="margin-input"
              />
              <span class="margin-label">上</span>
            </div>
            <div class="margin-sides">
              <div class="margin-left">
                <input
                  v-model.number="marginLeft"
                  type="number"
                  min="0"
                  max="100"
                  class="margin-input"
                />
                <span class="margin-label">左</span>
              </div>
              <div class="paper-preview">
                <div class="content-area"></div>
              </div>
              <div class="margin-right">
                <input
                  v-model.number="marginRight"
                  type="number"
                  min="0"
                  max="100"
                  class="margin-input"
                />
                <span class="margin-label">右</span>
              </div>
            </div>
            <div class="margin-bottom">
              <input
                v-model.number="marginBottom"
                type="number"
                min="0"
                max="100"
                class="margin-input"
              />
              <span class="margin-label">下</span>
            </div>
          </div>
        </div>
        
        <div class="margin-presets">
          <button 
            v-for="preset in marginPresets"
            :key="preset.name"
            @click="applyMarginPreset(preset)"
            class="preset-btn"
          >
            {{ preset.name }}
          </button>
        </div>
      </div>
    </ConfigSection>

    <!-- 打印设置 -->
    <ConfigSection title="打印设置" icon="🖨️">
      <div class="form-group">
        <label class="form-label">打印质量</label>
        <select v-model="printQuality" class="form-control">
          <option value="draft">草稿 (快速)</option>
          <option value="normal">普通</option>
          <option value="high">高质量</option>
        </select>
      </div>
      
      <div class="form-group">
        <label class="form-label">缩放比例</label>
        <div class="scale-control">
          <input
            v-model.number="printScale"
            type="range"
            min="50"
            max="200"
            step="5"
            class="scale-slider"
          />
          <span class="scale-value">{{ printScale }}%</span>
        </div>
      </div>
      
      <div class="form-group">
        <div class="checkbox-group">
          <label class="checkbox-item">
            <input v-model="fitToPage" type="checkbox" />
            <span class="checkbox-label">适应页面</span>
          </label>
          <label class="checkbox-item">
            <input v-model="centerOnPage" type="checkbox" />
            <span class="checkbox-label">居中打印</span>
          </label>
          <label class="checkbox-item">
            <input v-model="printBackground" type="checkbox" />
            <span class="checkbox-label">打印背景</span>
          </label>
        </div>
      </div>
    </ConfigSection>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import ConfigSection from './ConfigSection.vue'

export default {
  name: 'BasicConfig',
  components: {
    ConfigSection
  },
  
  setup() {
    const store = useStore()
    
    // 边距预设
    const marginPresets = [
      { name: '无边距', top: 0, right: 0, bottom: 0, left: 0 },
      { name: '窄边距', top: 5, right: 5, bottom: 5, left: 5 },
      { name: '普通', top: 10, right: 10, bottom: 10, left: 10 },
      { name: '宽边距', top: 20, right: 20, bottom: 20, left: 20 }
    ]
    
    // 纸张尺寸预设
    const paperSizes = {
      A4: { width: 210, height: 297 },
      A3: { width: 297, height: 420 },
      A5: { width: 148, height: 210 },
      Letter: { width: 216, height: 279 }
    }
    
    // 计算属性
    const templateName = computed({
      get: () => store.state.template.config.name,
      set: (value) => store.dispatch('template/updateConfig', { key: 'name', value })
    })
    
    const templateDescription = computed({
      get: () => store.state.template.config.description || '',
      set: (value) => store.dispatch('template/updateConfig', { key: 'description', value })
    })
    
    const paperSize = computed({
      get: () => store.state.template.config.paperSize,
      set: (value) => {
        store.dispatch('template/updateConfig', { key: 'paperSize', value })
        if (value !== 'custom' && paperSizes[value]) {
          const size = paperSizes[value]
          store.dispatch('template/updateConfig', { key: 'width', value: size.width })
          store.dispatch('template/updateConfig', { key: 'height', value: size.height })
        }
      }
    })
    
    const paperWidth = computed({
      get: () => store.state.template.config.width,
      set: (value) => store.dispatch('template/updateConfig', { key: 'width', value })
    })
    
    const paperHeight = computed({
      get: () => store.state.template.config.height,
      set: (value) => store.dispatch('template/updateConfig', { key: 'height', value })
    })
    
    const orientation = computed({
      get: () => store.state.template.config.orientation,
      set: (value) => store.dispatch('template/updateConfig', { key: 'orientation', value })
    })
    
    const marginTop = computed({
      get: () => store.state.template.config.marginTop,
      set: (value) => store.dispatch('template/updateConfig', { key: 'marginTop', value })
    })
    
    const marginRight = computed({
      get: () => store.state.template.config.marginRight || 10,
      set: (value) => store.dispatch('template/updateConfig', { key: 'marginRight', value })
    })
    
    const marginBottom = computed({
      get: () => store.state.template.config.marginBottom || 10,
      set: (value) => store.dispatch('template/updateConfig', { key: 'marginBottom', value })
    })
    
    const marginLeft = computed({
      get: () => store.state.template.config.marginLeft,
      set: (value) => store.dispatch('template/updateConfig', { key: 'marginLeft', value })
    })
    
    const printQuality = computed({
      get: () => store.state.template.config.printQuality || 'normal',
      set: (value) => store.dispatch('template/updateConfig', { key: 'printQuality', value })
    })
    
    const printScale = computed({
      get: () => store.state.template.config.printScale || 100,
      set: (value) => store.dispatch('template/updateConfig', { key: 'printScale', value })
    })
    
    const fitToPage = computed({
      get: () => store.state.template.config.fitToPage || false,
      set: (value) => store.dispatch('template/updateConfig', { key: 'fitToPage', value })
    })
    
    const centerOnPage = computed({
      get: () => store.state.template.config.centerOnPage || false,
      set: (value) => store.dispatch('template/updateConfig', { key: 'centerOnPage', value })
    })
    
    const printBackground = computed({
      get: () => store.state.template.config.printBackground || false,
      set: (value) => store.dispatch('template/updateConfig', { key: 'printBackground', value })
    })
    
    // 方法
    const applyMarginPreset = (preset) => {
      marginTop.value = preset.top
      marginRight.value = preset.right
      marginBottom.value = preset.bottom
      marginLeft.value = preset.left
    }
    
    return {
      marginPresets,
      templateName,
      templateDescription,
      paperSize,
      paperWidth,
      paperHeight,
      orientation,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
      printQuality,
      printScale,
      fitToPage,
      centerOnPage,
      printBackground,
      applyMarginPreset
    }
  }
}
</script>

<style scoped>
.basic-config {
  space-y: 24px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #495057;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control:disabled {
  background-color: #f8f9fa;
  opacity: 0.6;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.radio-group {
  display: flex;
  gap: 16px;
}

.radio-item {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.radio-item input[type="radio"] {
  margin-right: 8px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.radio-icon {
  font-size: 16px;
}

/* 边距可视化控件 */
.margin-controls {
  space-y: 16px;
}

.margin-visual {
  margin-bottom: 16px;
}

.margin-preview {
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 8px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.margin-top,
.margin-bottom {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.margin-sides {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 8px;
  align-items: center;
}

.margin-left,
.margin-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.margin-input {
  width: 60px;
  padding: 4px 6px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  text-align: center;
  font-size: 12px;
}

.margin-label {
  font-size: 11px;
  color: #6c757d;
  font-weight: 500;
}

.paper-preview {
  background: white;
  border: 2px dashed #28a745;
  border-radius: 4px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
}

.content-area {
  background: linear-gradient(45deg, #f8f9fa 25%, transparent 25%),
              linear-gradient(-45deg, #f8f9fa 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #f8f9fa 75%),
              linear-gradient(-45deg, transparent 75%, #f8f9fa 75%);
  background-size: 8px 8px;
  width: 80%;
  height: 60%;
  border-radius: 2px;
}

.margin-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 6px 12px;
  border: 1px solid #ced4da;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.preset-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

/* 缩放控件 */
.scale-control {
  display: flex;
  align-items: center;
  gap: 12px;
}

.scale-slider {
  flex: 1;
}

.scale-value {
  font-weight: 500;
  color: #495057;
  min-width: 50px;
  text-align: right;
}

/* 复选框组 */
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-item input[type="checkbox"] {
  margin-right: 8px;
}

.checkbox-label {
  font-size: 14px;
  color: #495057;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .radio-group {
    flex-direction: column;
    gap: 8px;
  }
  
  .margin-presets {
    justify-content: center;
  }
}
</style>
